import React, { useEffect, useState, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ExchangeCalculator from './components/ExchangeCalculator';
import CounterExchangeCalculator from './components/CounterExchangeCalculator';
import { Box, Link, Typography, Container } from '@mui/material';
import { LanguageProvider } from './components/LanguageContext';
import LanguageSelector from './components/LanguageSelector';
import { useLanguage } from './components/LanguageContext';
import { BrowserRouter as Router, Route, Routes, Link as RouterLink, useParams, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { loadRatesData } from './utils/ratesData';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3D52A0',
    },
    secondary: {
      main: '#7091E6',
    },
    background: {
      default: '#ffffff',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          borderRadius: '4px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'transparent',
            },
          },
        },
      },
    },
  },
});

// Map language codes to locale codes used in lang attribute
const localeMap = {
  english: 'en',
  hindi: 'hi',
  bengali: 'bn',
  tamil: 'ta',
  telugu: 'te',
  marathi: 'mr',
  kannada: 'kn',
  malayalam: 'ml',
  gujarati: 'gu',
  punjabi: 'pa',
};

// Component to display translated content
const TranslatedContent = () => {
  const { translation, currentLanguage, languages } = useLanguage();
  const currentLocale = localeMap[currentLanguage] || 'en'; // Get locale code
  const currentLanguageName = languages.find(lang => lang.code === currentLanguage)?.name || 'English';
  const params = useParams();
  const [currencyNames, setCurrencyNames] = useState({});

  // Dynamic title and description based on route
  let pageTitle = `${translation.exchangeCalculator || 'INR Currency Converter'} | ${currentLanguageName} | INRConverter.in`;
  let metaDescription = `${translation.homeDesc1 || 'Free INR currency converter...'}`.substring(0, 160); // Truncate if needed

  if (params.from && params.to) {
    const baseCurrency = params.from.toUpperCase();
    const targetCurrency = params.to.toUpperCase();
    const baseName = currencyNames[baseCurrency] || baseCurrency;
    const targetName = currencyNames[targetCurrency] || targetCurrency;
    pageTitle = `1 ${baseCurrency} to ${targetCurrency} - Convert ${baseName} to ${targetName} | INRConverter.in`;
    metaDescription = `Convert ${baseName} (${baseCurrency}) to ${targetName} (${targetCurrency}) with our free currency converter. Get real-time exchange rates and calculate between ${baseCurrency} and ${targetCurrency}.`.substring(0, 160);
  }

  // Fetch currency names for SEO if we're on a currency pair page
  useEffect(() => {
    if (params.from && params.to) {
      loadRatesData()
        .then(data => setCurrencyNames(data.currencies || {}))
        .catch(error => console.error('Error fetching currency names for SEO:', error));
    }
  }, [params.from, params.to]);

  useEffect(() => {
    console.log("Current route params:", params);
  }, [params]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Helmet>
        <html lang={currentLocale} />
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {params.from && params.to && (
          <>
            <link rel="canonical" href={`https://inrconverter.in/${params.from.toLowerCase()}-to-${params.to.toLowerCase()}`} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={`https://inrconverter.in/${params.from.toLowerCase()}-to-${params.to.toLowerCase()}`} />
          </>
        )}
      </Helmet>
      <Box
        sx={{
          bgcolor: '#3D52A0',
          borderBottom: '3px solid rgb(0, 0, 0)',
        }}
      >
        <Box
          component="header"
          sx={{
            p: { xs: 2, sm: 3, md: 3.5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{
              color: '#FFFFFF',
              fontFamily: "sans-serif",
              fontSize: { xs: '28px', sm: '36px', md: '48px' },
              fontWeight: 'bold',
              fontStyle: 'italic',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'none',
                opacity: 0.8,
              },
            }}
          >
            INR Converter
          </Link>
          <LanguageSelector />
        </Box>
      </Box>
      <Container component="main" sx={{ flex: 1, py: { xs: 2, sm: 4 } }}>
        <Routes>
          <Route path="/" element={<HomePageContent translation={translation} />} />
          <Route path="/debug" element={<DebugRoute />} />
          <Route path="/about" element={<AboutPage translation={translation} />} />
          <Route path="/privacy" element={<PrivacyPage translation={translation} />} />
          <Route path="/terms" element={<TermsPage translation={translation} />} />
          <Route path="/:from-to-:to" element={<HomePageContent translation={translation} />} />
          <Route path="*" element={<HomePageContent translation={translation} />} />
        </Routes>
      </Container>
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
          borderTop: '1px solid',
          borderColor: (theme) => theme.palette.divider,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/" variant="body2" color="text.secondary">
              {translation.home || 'Home'}
            </Link>
            <Link component={RouterLink} to="/about" variant="body2" color="text.secondary">
              {translation.aboutUs || 'About Us'}
            </Link>
            <Link component={RouterLink} to="/privacy" variant="body2" color="text.secondary">
              {translation.privacyPolicy || 'Privacy Policy'}
            </Link>
            <Link component={RouterLink} to="/terms" variant="body2" color="text.secondary">
              {translation.termsOfService || 'Terms of Service'}
            </Link>
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            {translation.copyright?.replace('{year}', new Date().getFullYear()) || `© ${new Date().getFullYear()} INRConverter.in. All rights reserved.`}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

// Conversion Tables component to display common currency conversions
const ConversionTables = ({ fromCurrency, toCurrency, exchangeRate }) => {
  // Values to display in the tables
  const fromValues = [1, 5, 10, 25, 50, 100, 500, 1000, 5000, 10000];
  
  // Function to format currency values
  const formatCurrency = (value, decimals = 2) => {
    // For small values (like INR to USD), show more decimals
    const actualDecimals = value < 0.1 ? 6 : decimals;
    return Number(value).toLocaleString(undefined, { 
      minimumFractionDigits: 0,
      maximumFractionDigits: actualDecimals
    });
  };
  
  return (
    <Box sx={{ width: '100%', mt: 6, mb: 6 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 4, md: 2 },
          justifyContent: 'space-between'
        }}
      >
        {/* Left table: From Currency to To Currency */}
        <Box sx={{ flex: 1, bgcolor: '#f5f7fa', borderRadius: 2, overflow: 'hidden' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              p: 2, 
              pb: 3, 
              textAlign: 'center', 
              fontWeight: 600,
              color: '#333'
            }}
          >
            Convert {fromCurrency} to {toCurrency}
          </Typography>
          
          <Box>
            {fromValues.map((value) => (
              <Box 
                key={`${fromCurrency}-${value}`}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  p: 2,
                  borderTop: '1px solid #e0e0e0'
                }}
              >
                <Typography sx={{ color: '#0066cc', fontWeight: 500 }}>
                  {value} {fromCurrency}
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {formatCurrency(value * exchangeRate)} {toCurrency}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        
        {/* Right table: To Currency to From Currency */}
        <Box sx={{ flex: 1, bgcolor: '#f5f7fa', borderRadius: 2, overflow: 'hidden' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              p: 2, 
              pb: 3, 
              textAlign: 'center', 
              fontWeight: 600,
              color: '#333'
            }}
          >
            Convert {toCurrency} to {fromCurrency}
          </Typography>
          
          <Box>
            {fromValues.map((value) => (
              <Box 
                key={`${toCurrency}-${value}`}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  p: 2, 
                  borderTop: '1px solid #e0e0e0'
                }}
              >
                <Typography sx={{ color: '#0066cc', fontWeight: 500 }}>
                  {value} {toCurrency}
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {formatCurrency(value / exchangeRate)} {fromCurrency}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// New component for the Home page content
const HomePageContent = ({ translation }) => {
  const params = useParams();
  const location = useLocation();
  const [currencyNames, setCurrencyNames] = useState({});
  const [exchangeRate, setExchangeRate] = useState(null);

  // Determine the currency pair to display in the h1 tag
  const currencyPair = useMemo(() => {
    if (params.from && params.to) {
      return { from: params.from.toUpperCase(), to: params.to.toUpperCase() };
    }
    const match = location.pathname.match(/\/([a-zA-Z]{3})-to-([a-zA-Z]{3})$/);
    if (match) {
      return { from: match[1].toUpperCase(), to: match[2].toUpperCase() };
    }
    return null;
  }, [params.from, params.to, location.pathname]);
  
  // Fetch currency names and exchange rates if we're on a currency pair page
  useEffect(() => {
    if (location.pathname.includes('-to-')) {
      const fetchData = async () => {
        try {
          const data = await loadRatesData();
          setCurrencyNames(data.currencies || {});

          if (data && data.rates && currencyPair) {
              const base = data.base;
              const from = currencyPair.from;
              const to = currencyPair.to;
              
              let rate;
              // Direct, inverse, or cross conversion depending on the base currency
              if (from === base) {
                rate = data.rates[to]; // Direct conversion
              } else if (to === base) {
                rate = 1 / data.rates[from]; // Inverse conversion
              } else {
                rate = data.rates[to] / data.rates[from]; // Cross conversion
              }
              
              setExchangeRate(rate);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };

      fetchData();
    }
  }, [location.pathname, currencyPair]);
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2, sm: 4, md: 10 },
        bgcolor: '#FFFFFF',
        gap: { xs: 8, sm: 10, md: 15 },
        flexDirection: 'column'
      }}
    >
      {/* Show description on all pages */}
      <Typography variant="body1" align="center" sx={{ mb: 4, maxWidth: '100%' }}>
        {(translation.homeDesc1 || 'Welcome...').split('. ').map((sentence, index, arr) => (
          <React.Fragment key={index}>{sentence}{index < arr.length - 1 ? '.' : ''}{index < arr.length - 1 && <br/>}</React.Fragment>
        ))}
      </Typography>
      
      {/* Currency conversion h1 tag placed below the intro paragraph */}
      {location.pathname.includes('-to-') && (
        <Box component="header" sx={{ width: '100%', mb: 4 }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
              color: '#333'
            }}
          >
            {currencyPair ? (
              `1 ${currencyPair.from} to ${currencyPair.to} - Convert ${currencyNames[currencyPair.from] || currencyPair.from} to ${currencyNames[currencyPair.to] || currencyPair.to}`
            ) : (
              `1 INR to USD - Convert Indian Rupee to US Dollar`
            )}
          </Typography>
        </Box>
      )}

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        maxWidth: '700px'
      }}>
        <Typography variant="h5" sx={{
          color: '#000000',
          fontWeight: 'bold',
          fontSize: { xs: '1.2rem', sm: '1.5rem' }
        }}>
          {translation.exchangeCalculator}
        </Typography>
        <ExchangeCalculator />
      </Box>
      
      {/* Add Conversion Tables for currency pair pages */}
      {location.pathname.includes('-to-') && currencyPair && exchangeRate && (
        <ConversionTables 
          fromCurrency={currencyPair.from} 
          toCurrency={currencyPair.to} 
          exchangeRate={exchangeRate} 
        />
      )}

      {/* Always show counter exchange calculator */}
      <Typography variant="body1" align="center" sx={{ mt: 2, mb: 2, maxWidth: '700px' }}>
        {translation.counterExchangeDesc}
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        maxWidth: '700px'
      }}>
        <Typography variant="h5" sx={{
          color: '#000000',
          fontWeight: 'bold',
          fontSize: { xs: '1.2rem', sm: '1.5rem' }
        }}>
          {translation.counterRates}
        </Typography>
        <CounterExchangeCalculator />
      </Box>
      <Typography variant="body2" align="center" sx={{ mt: 4, maxWidth: '800px', color: 'text.secondary' }}>
        {(translation.homeDesc2 || 'Disclaimer...').split('. ').map((sentence, index, arr) => (
          <React.Fragment key={index}>{sentence}{index < arr.length - 1 ? '.' : ''}{index < arr.length - 1 && <br/>}</React.Fragment>
        ))}
      </Typography>
    </Box>
  );
};

// Placeholder components for new pages
const AboutPage = ({ translation }) => (
    <Container sx={{py: 4}}>
        <Typography variant="h4" gutterBottom>{translation.aboutTitle || 'About Us'}</Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {translation.aboutContent || 'INRConverter.in is dedicated...'}
        </Typography>
    </Container>
);

// Simple debug component to test routing
const DebugRoute = () => (
  <Box p={4} sx={{ textAlign: 'center' }}>
    <Typography variant="h4">Debug Route Works!</Typography>
    <Typography variant="body1">Router is working correctly.</Typography>
  </Box>
);

const PrivacyPage = ({ translation }) => (
    <Container sx={{py: 4}}>
        <Typography variant="h4" gutterBottom>{translation.privacyTitle || 'Privacy Policy'}</Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {translation.privacyContent || 'Your privacy is important...'}
        </Typography>
    </Container>
);

const TermsPage = ({ translation }) => (
    <Container sx={{py: 4}}>
        <Typography variant="h4" gutterBottom>{translation.termsTitle || 'Terms of Service'}</Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {translation.termsContent || 'By using INRConverter.in...'}
        </Typography>
    </Container>
);

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LanguageProvider>
            <TranslatedContent />
          </LanguageProvider>
        </ThemeProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App; 
